import '../index.scss'
import names from '../names.json'
import Input from '../../../components/core/input'
import {Formik} from 'formik'
import CustomSelect from '../../../components/core/customSelect'
import Textarea from '../../../components/core/textarea'
import Switch from '../../../components/core/switch'
import {useState, useEffect, useRef} from 'react'
import {HelpIcon} from '../../../icons'
import InputInfo from '../../../components/core/inputInfo'
import LimitManager from '../../../utils/limitManager'
import {settingsFormRef} from '../refs'
import useOnClickOutside from '../../../utils/hooks/useOutsideClick'
import {statuspageSettingsSchema} from '../../../utils/validation'
import {useSelector} from 'react-redux'
import renderSkeletonLoaders from '../../../utils/loader'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../../utils/env'

const SettingsForm = ({
    id,
    statuspageData,
    darkThemeEnabled,
    setDarkThemeEnabled,
    isLimitExceeded,
    isDarkMode
}) => {
    const [active, setActive] = useState(null)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [isReinitialized, setReinitialized] = useState(true)
    const text = useNamespaceTranslations('statuspages')
    const validationText = useNamespaceTranslations('validation')
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const darkThemeInfoRef = useRef()
    const isProcessing = useSelector((state) => state.statuspage.isProcessing)

    const typeOptions = {
        public: {
            value: 'public',
            label: text.public
        },
        private: {
            value: 'private',
            label: LimitManager.getStatusPagePrivatePageEnabled(usageLimitsData)
                ? text.private
                : `${text.private_premium}`,
            disabled: !LimitManager.getStatusPagePrivatePageEnabled(usageLimitsData)
        }
    }

    const initialFormValues = {
        name: id ? statuspageData?.common?.name : '',
        description: id ? statuspageData?.common?.description : '',
        subdomain: id ? statuspageData?.common?.subdomain : '',
        type: id
            ? Object.values(typeOptions)?.find(
                (item) => item.value === statuspageData?.common?.type
            )
            : typeOptions.public,
        ip_addresses: id ? statuspageData?.common?.ip_addresses?.join('\n') : '',
        password: id ? statuspageData?.common?.password : ''
    }

    useOnClickOutside(darkThemeInfoRef, () =>
        setActive(active === names.dark_theme && '')
    )

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const handleReinitialize = () => {
        setReinitialized(false)
    }

    useEffect(() => {
        setIsFirstLoad(false)
    }, [])

    useEffect(() => {
        if (id) {
            setDarkThemeEnabled(statuspageData?.theme?.dark)
        }
    }, [JSON.stringify(statuspageData)])

    useEffect(() => {
        const formik = settingsFormRef.current
        if (!formik) return
        if (isFirstLoad) return

        (async() => {
            const schemaErrors = await formik.validateForm(formik.values)
            const current = formik.errors || {}

            const serverOnly = {}
            for (const k of Object.keys(current)) {
                if (!schemaErrors || !(k in schemaErrors)) {
                    serverOnly[k] = current[k]
                }
            }

            formik.setErrors({...(schemaErrors || {}), ...serverOnly})
        })()
    }, [validationText])

    return (
        <div className="content settings">
            <Formik
                innerRef={settingsFormRef}
                initialValues={initialFormValues}
                validateOnBlur={false}
                validateOnChange={false}
                enableReinitialize={isReinitialized}
                validationSchema={statuspageSettingsSchema}
            >
                {({values, errors, handleChange, handleSubmit, setFieldValue}) => (
                    <form onSubmit={handleSubmit} className="form-elements" onClick={handleReinitialize}>
                        {isProcessing ?  renderSkeletonLoaders(3) :
                            <>
                                <Input
                                    name={names.name}
                                    value={values.name}
                                    error={errors.name}
                                    placeholder={text.name_placeholder}
                                    infoText={text.name_info}
                                    onChange={handleChange}
                                    activeInfo={active === names.name}
                                    handleInfo={(state) => handleInfo(names.name, state)}
                                    inputIsDisabled={isLimitExceeded && !id}
                                />
                                <Input
                                    name={names.subdomain}
                                    value={values.subdomain}
                                    error={errors.subdomain}
                                    placeholder={text.subdomain_placeholder}
                                    infoText={text.subdomain_info}
                                    onChange={handleChange}
                                    pinnedPart={`.${envConfig.externalStatuspageDomain}`}
                                    activeInfo={active === names.subdomain}
                                    handleInfo={(state) => handleInfo(names.subdomain, state)}
                                    inputIsDisabled={isLimitExceeded && !id}
                                />
                                <CustomSelect
                                    name={names.type}
                                    value={values.type}
                                    error={errors.type}
                                    options={Object.values(typeOptions)}
                                    placeholder={text.type_placeholder}
                                    infoText={text.type_info}
                                    onChange={(name, data) => {
                                        setFieldValue(name, data)
                                        if (data.value === typeOptions?.public?.value) {
                                            setFieldValue(names.ip_addresses, '')
                                            setFieldValue(names.password, '')
                                        }
                                    }}
                                    activeInfo={active === names.type}
                                    handleInfo={(state) => handleInfo(names.type, state)}
                                    isDisabled={isLimitExceeded && !id}
                                />
                            </>}
                        {values?.type?.value === typeOptions?.private?.value && (
                            <>
                                {isProcessing ?  renderSkeletonLoaders(1, 'textarea-skeleton') :
                                    <Textarea
                                        type="text"
                                        name={names.ip_addresses}
                                        value={values.ip_addresses}
                                        error={errors.ip_addresses}
                                        placeholder={text.ip_addresses_placeholder}
                                        infoText={text.ip_addresses_info}
                                        onChange={(e) => {
                                            handleChange(e)
                                            setFieldValue(names.ip_addresses, e.target.value)
                                        }}
                                        activeInfo={active === names.ip_addresses}
                                        handleInfo={(state) => handleInfo(names.ip_addresses, state)}
                                        isDisabled={isLimitExceeded && !id}
                                    />}
                                {isProcessing ?  renderSkeletonLoaders(3) :
                                    <Input
                                        value={values.password}
                                        placeholder={text.password}
                                        infoText={text.password_info}
                                        name={names.password}
                                        onChange={handleChange}
                                        error={errors.password}
                                        activeInfo={active === names.password}
                                        handleInfo={(state) => handleInfo(names.password, state)}
                                        inputIsDisabled={isLimitExceeded && !id}
                                    />}
                            </>
                        )}
                        {isProcessing ?  renderSkeletonLoaders(1, 'switch-skeleton') :
                            <div className={classNames('subscription', {
                                dark: isDarkMode
                            })}>
                                <Switch
                                    name={names.dark_theme}
                                    checked={darkThemeEnabled}
                                    onChange={() => setDarkThemeEnabled(!darkThemeEnabled)}
                                    isDisabled={isLimitExceeded && !id}
                                />
                                <p>{text.theme_placeholder}</p>
                                <div
                                    ref={darkThemeInfoRef}
                                    className={`help ${
                                        active === names.dark_theme ? 'active' : ''
                                    }`}
                                    onClick={() =>
                                        setActive(active === names.dark_theme ? '' : names.dark_theme)
                                    }
                                >
                                    <span>
                                        <HelpIcon />
                                    </span>
                                    <InputInfo text={text.theme_info} />
                                </div>
                            </div>}
                    </form>
                )}
            </Formik>
        </div>
    )
}

export default SettingsForm
